import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../utils/api';
import { fetchJsonWithRetry } from '../utils/apiWithRetry';
import { logCategoryImpression, logCategoryInteraction } from '../utils/tracking';
import { getCategoryIcon } from '../components/icons';
import '../styles/design-system.css';
import './Home.css';

function Home() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loggedImpressionsRef = useRef(new Set());

  useEffect(() => {
    // Fetch categories from API
    const apiUrl = buildApiUrl('/api/categories/');
    console.log('Fetching categories from:', apiUrl);
    
    fetchJsonWithRetry(apiUrl, {}, 3)
      .then(data => {
        console.log('Categories loaded:', data.length);
        setCategories(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching categories:', error);
        setLoading(false);
      });
  }, []);

  // Track category impressions when categories become visible
  const observerSetupRef = useRef(false);
  
  useEffect(() => {
    if (categories.length === 0 || loading) return;

    if (observerSetupRef.current) return;
    observerSetupRef.current = true;

    const categoryElements = document.querySelectorAll('.category-card');
    
    if (categoryElements.length === 0) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[Home] Page hidden - pausing impression tracking');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const impressionObserver = new IntersectionObserver(
      (entries) => {
        if (document.hidden) {
          return;
        }
        
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const categoryElement = entry.target;
            const categoryIndex = parseInt(categoryElement.dataset.categoryIndex, 10);
            const category = categories[categoryIndex];
            
            const impressionKey = category?.category_id ? `${category.category_id}-${categoryIndex}` : null;
            
            if (!impressionKey || loggedImpressionsRef.current.has(impressionKey)) {
              return;
            }
            
            if (category && category.category_id) {
              loggedImpressionsRef.current.add(impressionKey);
              
              const visibilityRatio = entry.intersectionRatio;
              const viewportPosition = visibilityRatio >= 0.9 ? 'fully_visible' : 
                                      visibilityRatio >= 0.75 ? 'mostly_visible' : 
                                      'partially_visible';
              
              logCategoryImpression(
                category.category_id,
                categoryIndex,
                viewportPosition
              );
              
              impressionObserver.unobserve(categoryElement);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px'
      }
    );

    categoryElements.forEach((categoryElement, index) => {
      const category = categories[index];
      const impressionKey = category?.category_id ? `${category.category_id}-${index}` : null;
      
      if (impressionKey && loggedImpressionsRef.current.has(impressionKey)) {
        return;
      }
      
      impressionObserver.observe(categoryElement);
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (impressionObserver) {
        categoryElements.forEach((element) => {
          impressionObserver.unobserve(element);
        });
        impressionObserver.disconnect();
      }
      observerSetupRef.current = false;
    };
  }, [categories, loading]);

  const handleCategoryClick = (category) => {
    // Log interaction before navigating
    logCategoryInteraction(category.category_id, 'select', {
      category_key: category.category_key,
      category_name: category.name
    });
    
    navigate(`/category/${category.category_key}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="home-header-content">
          <h1 className="home-title">Fast & Slow</h1>
          <div className="user-info">
            <span className="user-name">Welcome, {user?.full_name || user?.email}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="home-main">
        <h2 className="categories-title">Choose a Category</h2>
        
        <div className="categories-grid">
          {categories.map((category, index) => (
            <div
              key={category.category_id}
              className="category-card"
              data-category-index={index}
              onClick={() => handleCategoryClick(category)}
            >
              <div className="category-icon">
                {getCategoryIcon(category.category_key, 64, '#1A1A1A')}
              </div>
              <h3 className="category-name">{category.name}</h3>
              {category.description && (
                <p className="category-description">{category.description}</p>
              )}
            </div>
          ))}
        </div>
      </main>

      <footer className="home-footer">
        <p>VKA Prototype - Beta</p>
      </footer>
    </div>
  );
}

export default Home;

